import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import { DirectorDocumentDTO, DocumentRequestDTO } from "@zenith/services/user";
import { removeBase64Prefix } from "@zenith/utils/Formaters/removeBase64Prefix";
import { getFileExtension } from "@zenith/utils/Helpers/getFileExtension";
import { Dayjs } from "dayjs";
import { CitizenForm } from "./citizen-form-component/citizen-form.model";
import { ForeignerForm } from "./foreigner-form-component/foreigner-form.model";
import { Directors, DocConfig } from "./interfaces";

const getExtension = (mime: string) => {
  const extensions: { [k: string]: string } = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "application/pdf": "pdf",
    default: "jpg",
  };
  return extensions[mime] || extensions["default"];
};

export const base64toFile = (
  dataurl: string,
  filename: string
): File | undefined => {
  if (!dataurl) {
    return undefined;
  }
  const arr = dataurl.split(",");
  const mime = arr[0]?.match(/:(.*?);/)?.[1];
  const bstr = window.atob(arr[arr.length - 1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  const name = `${filename}.${getExtension(mime ?? "")}`;
  return new File([u8arr], name, { type: mime });
};

export const directorAlreadyExists = (directors: Directors, bvn?: string) => {
  if (!bvn) {
    return true;
  }
  return directors.some((item) => item.bvn === bvn);
};

export const selectItems: SelectItemsType[] = [
  {
    label: "Yes, he/she is a foreigner",
    value: "foreigner",
  },
  {
    label: "No, he/she is a citizen of Nigeria",
    value: "citizen",
  },
];

export const mapToDocuments = (
  docProperties: DocConfig[],
  form: Partial<ForeignerForm> & Partial<CitizenForm>
) => {
  return docProperties
    .map((item) => ({
      documentNumber: form[item.documentNumber] as string,
      documentType: item.documentType,
      contents: item.contents
        .map((i) => {
          const docFile = base64toFile(
            form[i.imageContent] as string,
            "my-file"
          );
          const extension = docFile ? getFileExtension(docFile) : "";
          return {
            imageContent: removeBase64Prefix(
              form[i.imageContent] as string,
              extension
            ),
            imageType: i.imageType,
            mimeExt: extension,
          };
        })
        .filter((item) => !!item.imageContent),
      ...(item.expiryDate
        ? { expiryDate: (form[item.expiryDate] as Dayjs)?.format("YYYY-MM-DD") }
        : {}),
    }))
    .filter((doc) => doc.contents.length);
};

export const ForeignerDocConfig: DocConfig[] = [
  {
    documentNumber: "passportNumber",
    documentType: DirectorDocumentDTO.documentType.INTERNATIONAL_PASSPORT,
    expiryDate: "passportExpiryDate",
    contents: [
      {
        imageContent: "passportDoc",
      },
    ],
  },
  {
    documentNumber: "residentPermitNumber",
    documentType: DirectorDocumentDTO.documentType.RESIDENT_PERMIT,
    expiryDate: "residentPermitExpiryDate",
    contents: [
      {
        imageContent: "residentPermitDoc",
      },
    ],
  },
];

export const CitizenDocConfig: DocConfig[] = [
  {
    documentNumber: "driverLicenceNumber",
    documentType: DirectorDocumentDTO.documentType.DRIVERS_LICENSE,
    expiryDate: "driverLicenceExpiryDate",
    contents: [
      {
        imageContent: "driverLicenceFront",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_FRONT_BASE64,
      },
      {
        imageContent: "driverLicenceBack",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_BACK_BASE64,
      },
    ],
  },
  {
    documentNumber: "votersNumber",
    documentType: DirectorDocumentDTO.documentType.VOTERS_CARD,
    contents: [
      {
        imageContent: "votersCardFront",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_FRONT_BASE64,
      },
      {
        imageContent: "votersCardBack",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_BACK_BASE64,
      },
    ],
  },
  {
    documentNumber: "passportNumber",
    documentType: DirectorDocumentDTO.documentType.INTERNATIONAL_PASSPORT,
    expiryDate: "passportExpiryDate",
    contents: [
      {
        imageContent: "passportDocument",
      },
    ],
  },
  {
    documentNumber: "nin",
    documentType: DirectorDocumentDTO.documentType.NATIONAL_ID_CARD,
    contents: [
      {
        imageContent: "nationalIdCardFront",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_FRONT_BASE64,
      },
      {
        imageContent: "nationalIdCardBack",
        imageType: DocumentRequestDTO.imageType.DOCUMENT_BACK_BASE64,
      },
    ],
  },
];
